import { IEquipment } from '@frontend/equipment/types';
import { Application, Feature } from '@frontend/functionality/types';
import { CommonMessage } from '@frontend/lang';
import { PackageClient } from '@frontend/package/api';
import { Package, PackageStateName, PackageType } from '@frontend/package/types';
import { Slot } from '@frontend/slot/types';
import { TransactionActorClient, TransactionClient, TransactionWorkflowClient } from '@frontend/transaction/api';
import { Transaction, TransactionActorType, TransactionStateName, TransactionTriggerName, TransactionType } from '@frontend/transaction/types';
import React, { useCallback, useRef, useState } from 'react';

import { useIdentification } from '../../../hooks/useIdentification/useIdentification';
import { useAppSelector } from '../../../redux/store';

export interface CreateAuditTransactionParams {
    accountId: string;
    productId?: string;
    equipmentId?: string;
}

export interface CreateAuditTransactionFunctionality {
    id: string;
    application: Application;
    feature: Feature;
}

export interface CreateAuditTransactionWorkflowSettings {
    transactionWorkflowId: string;
    auditPackageWorkflowId: string;
}

export interface UseCreateAuditTransactionOptions {
    id: string;
    functionality: CreateAuditTransactionFunctionality | null;
    workflowSettings: CreateAuditTransactionWorkflowSettings;
    slot: Slot | null;
    onTransactionCreated?: (transaction: Transaction, auditPackage: Package) => Promise<void>;
    onComplete: () => void;
}

/** Wizard-only audit transaction hook — separate from manage-spot useAuditTransaction. */
export const useCreateAuditTransaction = (options: UseCreateAuditTransactionOptions) => {
    const { id, functionality, workflowSettings, slot, onTransactionCreated, onComplete } = options;
    const navigationState = useAppSelector((store) => store.navigation);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState<React.ReactNode | null>(null);
    const isSubmittingRef = useRef(false);

    const createTransactionWithPackage = useCallback(
        async (params: CreateAuditTransactionParams) => {
            if (!functionality?.id || !workflowSettings.transactionWorkflowId || !workflowSettings.auditPackageWorkflowId || !slot || isSubmittingRef.current) {
                return;
            }

            isSubmittingRef.current = true;
            setIsSubmitting(true);
            setError(null);

            try {
                const transaction = await TransactionClient.postTransaction(params.accountId, {
                    type: TransactionType.AUDIT,
                    user_id: navigationState.user?.id ?? null,
                    spot_id: slot.spot_id,
                    module_id: slot.module_id,
                    slot_id: slot.id,
                    application: functionality.application,
                    feature: functionality.feature,
                    workflow_id: workflowSettings.transactionWorkflowId,
                    state: TransactionStateName.TRANSACTION_CREATED
                });

                const auditPackage = await PackageClient.postPackage(transaction.account_id, transaction.id, {
                    type: PackageType.AUDIT,
                    product_id: params.productId ?? null,
                    equipment_id: params.equipmentId ?? null,
                    spot_id: slot.spot_id,
                    module_id: slot.module_id,
                    slot_id: slot.id,
                    quantity: 1,
                    workflow_id: workflowSettings.auditPackageWorkflowId,
                    user_id: navigationState.user?.id ?? null,
                    state: PackageStateName.PACKAGE_CREATED,
                    application: functionality.application,
                    feature: functionality.feature
                });

                if (navigationState.user?.id) {
                    await TransactionActorClient.postTransactionActor(transaction.account_id, transaction.id, {
                        type: TransactionActorType.DROPPED_OFF_BY,
                        user_id: navigationState.user.id
                    });
                }

                await TransactionWorkflowClient.triggerTransactionState(transaction.account_id, transaction.id, {
                    trigger: TransactionTriggerName.TRANSACTION_PROCESS,
                    location: navigationState.spot?.id ?? undefined,
                    actor: navigationState.user?.id ?? undefined
                });

                await onTransactionCreated?.(transaction, auditPackage);
                onComplete();
            } catch {
                setError(CommonMessage.ERRORS.DEFAULT);
            } finally {
                isSubmittingRef.current = false;
                setIsSubmitting(false);
            }
        },
        [functionality, navigationState.spot?.id, navigationState.user?.id, onComplete, onTransactionCreated, slot, workflowSettings]
    );

    useIdentification({
        id,
        onEquipmentIdentified: (equipment: IEquipment) => {
            if (isSubmittingRef.current) return;
            void createTransactionWithPackage({
                accountId: equipment.account_id,
                productId: equipment.product_id,
                equipmentId: equipment.id
            });
        }
    });

    return {
        isSubmitting,
        error,
        setError,
        createTransactionWithPackage
    };
};
